/**
 * 同盾（TD）工具函数
 * 提供获取 TD 参数的简单方法，类似于 CF Turnstile 的使用方式
 */

import { mapWebLangToServerLang } from "../i18n/utils"
import {
	getCurrentAreaInClient,
	getCurrentLanguageInClient,
} from "../utils.client"

/**
 * TD 参数接口
 */
export interface TdHeaders {
	"X-TONGDUN-PATH": string
	"X-TONGDUN-BLACKBOX": string
	"X-TONGDUN-SIGN": string
}

/**
 * 获取 TD 请求头参数
 * 类似于 CF Turnstile 的动态获取方式
 */
export function getTdHeaders(customPath?: string): Partial<TdHeaders> {
	const headers: Partial<TdHeaders> = {}

	try {
		if (typeof window === "undefined") {
			return headers
		}

		// 获取路径（优先使用传入的路径，否则使用当前页面路径）
		let path = customPath
		if (!path) {
			const currentPath = window.location.pathname
			path = currentPath.replace(/^\/[a-z]{2}\//, "/")
		}

		if (path) {
			headers["X-TONGDUN-PATH"] = path
		}

		// 获取 blackbox
		const blackbox =
			(window as any).__td_blackbox || localStorage.getItem("td_blackbox")
		if (blackbox) {
			headers["X-TONGDUN-BLACKBOX"] = blackbox
		}

		// 获取签名（使用指定的路径）
		const getSign = (window as any).__td_getSign
		if (typeof getSign === "function") {
			const sign = getSign(path || "auth")
			if (sign) {
				headers["X-TONGDUN-SIGN"] = sign
			}
		}
	} catch (error) {
		console.warn("[TD Utils] 获取 TD 参数失败:", error)
	}

	return headers
}

/**
 * 检查 TD 参数是否完整
 */
export function isTdComplete(headers: Partial<TdHeaders>): boolean {
	return !!(
		headers["X-TONGDUN-PATH"] &&
		headers["X-TONGDUN-BLACKBOX"] &&
		headers["X-TONGDUN-SIGN"]
	)
}

/**
 * 等待 TD 初始化的简单版本
 * 返回 Promise，解析为是否成功获取完整参数
 */
export function waitForTdHeaders(
	maxWaitMs: number = 3000,
	customPath?: string,
): Promise<Partial<TdHeaders>> {
	return new Promise((resolve) => {
		const startTime = Date.now()

		const checkTd = () => {
			const headers = getTdHeaders(customPath)

			// 如果获取到完整参数，立即返回
			if (isTdComplete(headers)) {
				resolve(headers)
				return
			}

			// 检查是否超时
			if (Date.now() - startTime >= maxWaitMs) {
				console.warn("[TD Utils] 等待 TD 参数超时，返回当前可用参数")
				resolve(headers)
				return
			}

			// 继续等待
			setTimeout(checkTd, 100)
		}

		checkTd()
	})
}

/**
 * 获取标准的认证请求头
 * 包含区域、语言和 TD 参数
 * 注意：此函数不会等待 TD 参数，如果 sign 为空可能返回不完整的头部
 */
export function getAuthHeaders(
	customPath?: string,
	additionalHeaders: Record<string, string> = {},
): Record<string, string> {
	const tdHeaders = getTdHeaders(customPath)

	return {
		// 基础头部
		"X-AREA": getCurrentAreaInClient(),
		"Accept-Language": mapWebLangToServerLang(getCurrentLanguageInClient()),

		// TD 头部
		...tdHeaders,

		// 额外的头部（如 CF Turnstile token）
		...additionalHeaders,
	}
}

/**
 * 获取标准的认证请求头（等待 TD 参数准备就绪）
 * 确保 sign 有值后才返回，适用于关键认证请求
 */
export async function getAuthHeadersWaitSign(
	customPath?: string,
	additionalHeaders: Record<string, string> = {},
	maxWaitMs: number = 3000,
): Promise<Record<string, string>> {
	// 等待 TD 参数，特别是 sign
	const tdHeaders = await waitForTdHeaders(maxWaitMs, customPath)

	// 检查同盾参数是否完整，如果不完整则抛出错误
	if (!isTdComplete(tdHeaders)) {
		const missingParams: string[] = []
		if (!tdHeaders["X-TONGDUN-PATH"]) missingParams.push("PATH")
		if (!tdHeaders["X-TONGDUN-BLACKBOX"]) missingParams.push("BLACKBOX")
		if (!tdHeaders["X-TONGDUN-SIGN"]) missingParams.push("SIGN")

		throw new Error(
			`[TD Utils] Tongdun parameters incomplete, missing: ${missingParams.join(", ")}`,
		)
	}

	return {
		// 基础头部
		"X-AREA": getCurrentAreaInClient(),
		"Accept-Language": mapWebLangToServerLang(getCurrentLanguageInClient()),

		// TD 头部
		...tdHeaders,

		// 额外的头部
		...additionalHeaders,
	}
}

/**
 * 等待并获取包含 TD 参数的认证请求头
 * 类似于等待 CF Turnstile token 的方式
 */
export async function getAuthHeadersAsync(
	additionalHeaders: Record<string, string> = {},
	maxWaitMs: number = 3000,
): Promise<Record<string, string>> {
	// 等待 TD 参数
	const tdHeaders = await waitForTdHeaders(maxWaitMs)

	return {
		// 基础头部
		"X-AREA": getCurrentAreaInClient(),
		"Accept-Language": mapWebLangToServerLang(getCurrentLanguageInClient()),

		// TD 头部
		...tdHeaders,

		// 额外的头部
		...additionalHeaders,
	}
}
